import React from 'react';
import {formatBlockClassificationPrefix} from '../../../utils/documentHeaderMeta';
import {shouldHideUnconfirmedPreviewNode} from './previewSuggestionContent';

// Shared helpers for list renderers

const isVisiblePrefixTarget = (child) => (
    (child?.type === 'paragraph' || child?.type === 'heading')
    && (child?.content?.length > 0)
    && !shouldHideUnconfirmedPreviewNode(child)
);

const findFirstPrefixTargetIndex = (children = []) => {
    for (let i = 0; i < (children?.length || 0); i++) {
        if (isVisiblePrefixTarget(children[i])) {
            return i;
        }
    }
    return -1;
};

/**
 * Get classification from the first visible paragraph/heading child of a list item,
 * since only paragraphs and headings carry classification level attributes.
 */
const getClassificationFromChildren = (liNode) => {
    const children = liNode?.content || [];
    const idx = findFirstPrefixTargetIndex(children);
    if (idx === -1) return 'U';
    return formatBlockClassificationPrefix(children[idx]?.attrs);
};

const visibleListItems = (node) => (
    (node?.content || []).filter((li) => !shouldHideUnconfirmedPreviewNode(li))
);

// Ordered list, bullet list and list item renderers. These use a renderNode
// callback passed from NodeRenderer to recursively render children.
export const OrderedList = ({node, idx, inTable = false, renderNode}) => {
    if (!node || shouldHideUnconfirmedPreviewNode(node)) return null;
    const items = visibleListItems(node);
    if (items.length === 0) return null;
    const key = `node-${idx}`;

    const start = (node.attrs && node.attrs.start) || 1;
    return (
        <ol key={idx} start={start}>
            {items.map((li, i) => {
                const firstIdx = findFirstPrefixTargetIndex(li?.content);
                const cls = firstIdx >= 0 ? `(${getClassificationFromChildren(li)})` : undefined;
                return (
                    <li key={`${key}-li-${i}`}>
                        {(li.content || []).map((c, j) => (
                            renderNode(c, [i, j], {
                                inTable,
                                inListItem: true,
                                prefixText: j === firstIdx ? cls : undefined
                            })
                        ))}
                    </li>
                );
            })}
        </ol>
    );
};

export const BulletList = ({node, idx, inTable = false, renderNode}) => {
    if (!node || shouldHideUnconfirmedPreviewNode(node)) return null;
    const items = visibleListItems(node);
    if (items.length === 0) return null;
    const key = `node-${idx}`;

    return (
        <ul key={key}>
            {items.map((li, i) => {
                const firstIdx = findFirstPrefixTargetIndex(li?.content);
                const cls = firstIdx >= 0 ? `(${getClassificationFromChildren(li)})` : undefined;
                return (
                    <li key={`${key}-bli-${i}`}>
                        {(li.content || []).map((c, j) => (
                            renderNode(c, [i, j], {
                                inTable,
                                inListItem: true,
                                prefixText: j === firstIdx ? cls : undefined
                            })
                        ))}
                    </li>
                );
            })}
        </ul>
    );
};

export const ListItem = ({node, idx, inTable = false, renderNode}) => {
    if (!node || shouldHideUnconfirmedPreviewNode(node)) return null;
    const key = `node-${idx}`;

    const firstIdx = findFirstPrefixTargetIndex(node?.content);
    const cls = firstIdx >= 0 ? `(${getClassificationFromChildren(node)})` : undefined;

    return (
        <li key={idx}>
            {(node.content || []).map((c, i) => (
                renderNode(c, [key, i], {
                    inTable,
                    inListItem: true,
                    prefixText: i === firstIdx ? cls : undefined
                })
            ))}
        </li>
    );
};

